import { useEffect, useState, Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Radio, ExternalLink, TrendingUp, Mail } from "lucide-react";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { SponsoredCard } from "../components/SponsoredCard";
import { AffiliateCard } from "../components/AffiliateCard";
import { useT } from "../lib/i18n";

const CATEGORIES = [
  "All", "Tech & AI Trends", "Creative Business", "Design & Visual Arts",
  "Innovation & Startups", "Lifestyle & Culture", "Inspiration & People", "Gaming", "Digital Culture",
];

function ArticlesSidebar({ ad, affiliate, category }) {
  const [mostRead, setMostRead] = useState([]);
  const [wireNow, setWireNow] = useState([]);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    api.get("/articles/most-read?days=7&limit=5").then((r) => setMostRead(r.data || [])).catch(() => {});
    api.get("/inkwire?tab=Latest").then((r) => setWireNow((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || subLoading) return;
    setSubLoading(true);
    try {
      await api.post("/newsletter", { email });
      setSubscribed(true);
      setEmail("");
    } catch { /* ignore */ } finally {
      setSubLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Advertisement */}
      {(affiliate || ad) && (
        <div data-testid="sidebar-ad">
          {affiliate
            ? <AffiliateCard ad={affiliate} placement="articles_sidebar" category={category} className="border-t-4 border-t-pti-red" />
            : <SponsoredCard ad={ad} label="Advertisement" />}
        </div>
      )}

      {/* Most Read This Week */}
      {mostRead.length > 0 && (
        <section data-testid="sidebar-most-read">
          <h2 className="flex items-center gap-2 font-display text-sm font-black uppercase tracking-widest border-b-2 border-pti-black pb-2">
            <TrendingUp size={15} className="text-pti-red" /> Most Read This Week
          </h2>
          <ol className="mt-3 divide-y divide-gray-100">
            {mostRead.map((a, i) => (
              <li key={a.id}>
                <Link to={`/article/${a.slug}`} data-testid={`most-read-${i}`}
                  className="group flex gap-3 py-3 items-start">
                  <span className="font-display text-lg font-black text-gray-300 leading-none w-6 shrink-0 group-hover:text-pti-red transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm font-semibold leading-snug group-hover:text-pti-red transition-colors line-clamp-2">
                    {a.title}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* InkWire Now */}
      {wireNow.length > 0 && (
        <section data-testid="sidebar-inkwire">
          <div className="flex items-center justify-between border-b-2 border-pti-black pb-2">
            <h2 className="flex items-center gap-2 font-display text-sm font-black uppercase tracking-widest">
              <Radio size={15} className="text-pti-red" /> InkWire Now
            </h2>
            <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
              <span className="w-1.5 h-1.5 rounded-full bg-pti-red animate-pulse" aria-hidden="true" /> Live
            </span>
          </div>
          <div className="mt-3 divide-y divide-gray-100">
            {wireNow.map((it, i) => (
              <a key={it.id || it.link} href={it.link} target="_blank" rel="noopener noreferrer" data-testid={`inkwire-now-${i}`}
                className="group block py-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-pti-red">{it.source}</div>
                <h3 className="text-sm font-semibold leading-snug mt-0.5 group-hover:text-pti-red transition-colors line-clamp-2">
                  {it.title}
                </h3>
              </a>
            ))}
          </div>
          <Link to="/inkwire" data-testid="sidebar-inkwire-more"
            className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-pti-black hover:text-pti-red transition-colors">
            View InkWire <ExternalLink size={13} />
          </Link>
        </section>
      )}

      {/* Newsletter */}
      <section className="bg-pti-black text-white p-5" data-testid="sidebar-newsletter">
        <div className="flex items-center gap-2 text-pti-red">
          <Mail size={16} />
          <span className="font-display text-xs font-black uppercase tracking-widest">The PictureThisInk Newsletter</span>
        </div>
        {subscribed ? (
          <p className="text-sm text-neutral-300 mt-3" data-testid="newsletter-success">
            You're in. Watch your inbox for the next edition.
          </p>
        ) : (
          <>
            <p className="text-sm text-neutral-400 mt-2">Ideas move. Culture follows. Get the best of PTI weekly.</p>
            <form onSubmit={subscribe} className="mt-3 space-y-2">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" aria-label="Email address" data-testid="newsletter-email"
                className="w-full bg-white/5 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-pti-red outline-none" />
              <button type="submit" disabled={subLoading} data-testid="newsletter-submit"
                className="w-full bg-pti-red text-white text-sm font-bold uppercase tracking-widest py-2 hover:bg-red-700 transition-colors disabled:opacity-60">
                {subLoading ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

export default function Articles() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [sidebarAd, setSidebarAd] = useState(null);
  const [sidebarAff, setSidebarAff] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("latest");
  const category = params.get("category") || "All";

  useEffect(() => {
    const url = `/articles?limit=100${category !== "All" ? `&category=${encodeURIComponent(category)}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}${tab === "featured" ? "&featured=true" : ""}`;
    api.get(url).then((r) => setArticles(r.data)).catch(() => {});
  }, [category, q, tab]);

  useEffect(() => {
    api.get("/ads/active?placement=homepage").then((r) => {
      const list = r.data || [];
      setSidebarAd(list.find((a) => /mishertech/i.test(a.brand_name)) || list[0] || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const c = encodeURIComponent(category);
    api.get(`/affiliate?placement=articles_sidebar&category=${c}`).then((r) => setSidebarAff(r.data[0] || null)).catch(() => {});
  }, [category]);

  const setCategory = (c) => { const p = new URLSearchParams(params); if (c === "All") p.delete("category"); else p.set("category", c); setParams(p); };

  return (
    <div className="mx-auto max-w-[1600px] px-5 sm:px-6 py-10" data-testid="articles-page">
      <header className="border-b-2 border-pti-black pb-4">
        <h1 className="font-display text-4xl sm:text-5xl font-black">{t("articles")}</h1>
        <p className="text-gray-600 mt-1">Original editorial reporting from PictureThisInk creators.</p>
      </header>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6">
        <div className="flex gap-6" role="tablist">
          {["latest", "featured"].map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`text-sm font-bold uppercase tracking-wider pb-1 border-b-2 transition-colors ${tab === t ? "border-pti-red text-pti-black" : "border-transparent text-gray-500 hover:text-pti-black"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="relative max-w-xs w-full">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" aria-label="Search articles"
            data-testid="articles-search" className="w-full border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-pti-red outline-none" />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto mt-5 pb-2" role="tablist" aria-label="Categories">
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c.replace(/\s|&/g, "")}`}
            className={`whitespace-nowrap px-3 py-1.5 text-xs font-bold border transition-colors ${category === c ? "bg-pti-black text-white border-pti-black" : "border-gray-300 text-gray-600 hover:border-pti-black"}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Desktop: article grid + sticky sidebar rail. Mobile: grid then sidebar stacked below. */}
      <div className="lg:flex lg:gap-10 mt-8">
        <div className="lg:flex-1 min-w-0">
          {articles.length === 0 ? (
            <p className="text-gray-400 py-20 text-center">No articles found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {articles.map((a) => (
                <Fragment key={a.id}>
                  <ArticleCard article={a} />
                </Fragment>
              ))}
            </div>
          )}
        </div>

        <aside className="mt-12 lg:mt-0 w-full lg:w-[340px] shrink-0" data-testid="articles-sidebar" aria-label="Article sidebar">
          <div className="lg:sticky lg:top-24">
            <ArticlesSidebar ad={sidebarAd} affiliate={sidebarAff} category={category} />
          </div>
        </aside>
      </div>
    </div>
  );
}
